import React, { useEffect, useState } from 'react';

import { useMediaQuery } from '@rbilabs/components-library';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';

import { ActionButton, ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { IDeliveryAddress } from 'generated/rbi-graphql';
import { useDistanceWarning } from 'hooks/use-distance-warning';
import { StoreLocatorSteps } from 'pages/store-locator/types';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isMobile } from 'utils/environment';
import { IPlaceAddress } from 'utils/geolocation';
import { convertCountryStringToIsoCountryCode2 } from 'utils/i18n';

import { useDeliveryAddressModalContext } from '../delivery-address-modal.context';
import { DeliveryTerms } from '../delivery-terms';
import { InfoBox } from '../info-box';
import { LoadingText } from '../new-address-form/delivery-address-form-footer';
import { RecentAddresses } from '../recent-addresses';
import { SavedAddresses } from '../recent-addresses/saved-addresses';
import { StyledModalContent, StyledModalHeader } from '../styled';
import { UseCurrentLocationButton } from '../use-current-location-button';

import { DeliveryAddressItem } from './delivery-address-item';
import { AddressList, ShareLocationButton, WarningContainer } from './styled';
import { IDeliveryAddressesProps } from './types';

export const DeliveryAddresses: React.FC<IDeliveryAddressesProps> = ({
  addresses,
  recentAddresses,
  setAddress,
  setCurrentStep,
  currentStep,
  isLoading: loading = false,
  showDeliveryAddresses = false,
  showRecentAddresses = false,
  onShareLocationClick,
  onUseCurrentLocationClick,
  onEditAddress,
}) => {
  const { isPermissionGranted } = useGeolocation();
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState(-1);
  const { trackEvent } = useCdpContext();
  const isDesktop = useMediaQuery('desktop');
  const enableDeliveryOnSameScreen = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_ADDRESS_ON_SAME_SCREEN
  );
  const amountDeliveryAddressSaved = useFlag(LaunchDarklyFlag.AMOUNT_DELIVERY_ADDRESS_SAVED);
  const enableDeliveryGeolocation = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_GEOLOCATION_2);
  const enableSavedAddressesImprovements = useFlag(
    LaunchDarklyFlag.ENABLE_SAVED_ADDRESSES_IMPROVEMENTS
  );

  const isSavedAddressesStep = currentStep === StoreLocatorSteps.SAVED_ADDRESS;
  const { setDeliveryAddress } = useDeliveryAddressModalContext();

  const { canSaveDeliveryAddress, setCanSaveDeliveryAddress } = useDeliveryAddressModalContext();

  const {
    isLocationConfirmationNeeded,
    ConfirmLocationModal,
    dismissDialog,
    openConfirmationModal,
    isLoading: isLoadingGetUserOrders,
    isGeolocationEnabled,
  } = useDistanceWarning();

  const isLoading = loading || isLoadingGetUserOrders;

  const setSelectedAddress = (address: IDeliveryAddress) => {
    const selectedAddressComponents: IPlaceAddress = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      city: address.city,
      state: address.state || '',
      streetNumber: address.streetNumber || '',
      route: address.route || '',
      zip: address.zip || '',
      country: address.country || '',
    };
    let addressCoordinates;

    if (address.latitude && address.longitude) {
      addressCoordinates = {
        lat: address.latitude,
        lng: address.longitude,
      };

      setDeliveryAddress({
        address: { ...selectedAddressComponents, location: 'recent' },
        coordinates: addressCoordinates,
      });
    }
    setAddress({
      address: selectedAddressComponents,
      phoneNumber: address.phoneNumber,
      shouldSaveAddress: false,
      coordinates: addressCoordinates,
    });
  };

  const onCancelDistanceWarning = () => {
    setSelected(-1);
    dismissDialog();
  };

  const onConfirmDistanceWarning = () => {
    setSelectedAddress(addresses[selected]);
    dismissDialog();
  };

  const distanceWarning = async (address: IDeliveryAddress) => {
    if (!address.latitude || !address.longitude) {
      setSelectedAddress(address);
      return;
    }

    const userCoordinates = {
      lat: address.latitude,
      lng: address.longitude,
    };

    const isFarAway = await isLocationConfirmationNeeded(userCoordinates);
    if (isFarAway) {
      openConfirmationModal();
      return;
    }
    setSelectedAddress(address);
  };

  const handleRecentAddressSelect = (address: IDeliveryAddress) => {
    trackEvent({
      name: CustomEventNames.SELECT_DELIVERY_RECENT_ADDRESS,
      type: EventTypes.Other,
      attributes: {
        'Address Type': 'recent',
      },
    });

    distanceWarning(address);
  };

  const handleSavedAddressSelect = (address: IDeliveryAddress) => {
    trackEvent({
      name: CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS,
      type: EventTypes.Other,
      attributes: {
        'Address Type': address.alias,
      },
    });

    distanceWarning(address);
  };

  const handleSavedAddressLegacy = () => {
    const address = addresses[selected];
    trackEvent({
      name: CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS,
      type: EventTypes.Other,
      attributes: {
        'Address Type': address?.alias || undefined,
      },
    });
    distanceWarning(address);
  };

  useEffect(() => {
    if (amountDeliveryAddressSaved !== 0 && addresses.length >= amountDeliveryAddressSaved) {
      setCanSaveDeliveryAddress(false);
    } else {
      setCanSaveDeliveryAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountDeliveryAddressSaved, addresses]);

  return (
    <>
      {(showRecentAddresses || !showDeliveryAddresses) && !isSavedAddressesStep && (
        <>
          <StyledModalContent data-testid="delivery-addresses">
            <StyledModalHeader $deliveryOnSameScreen={enableDeliveryOnSameScreen}>
              {formatMessage({ id: 'deliverySelectYourAddress' })}
            </StyledModalHeader>
          </StyledModalContent>
          <AddressList>
            {enableDeliveryGeolocation && (
              <UseCurrentLocationButton onClick={onUseCurrentLocationClick}>
                {!isPermissionGranted && (
                  <ShareLocationButton
                    data-testid="share-location"
                    onClick={onShareLocationClick}
                    variant={ActionButtonVariants.OUTLINE}
                    size={ActionButtonSizes.SMALL}
                  >
                    {formatMessage({ id: 'shareMyLocation' })}
                  </ShareLocationButton>
                )}
              </UseCurrentLocationButton>
            )}
            {!showRecentAddresses && (
              <RecentAddresses
                addresses={recentAddresses}
                onEditAddress={onEditAddress}
                onAddressSelect={handleRecentAddressSelect}
              />
            )}
            {!showDeliveryAddresses && enableSavedAddressesImprovements && (
              <AddressList>
                <SavedAddresses
                  key={`${currentStep}#${isMobile()}`}
                  addresses={addresses}
                  setAddress={handleSavedAddressSelect}
                  setCurrentStep={setCurrentStep}
                  initialCollapsed={!showRecentAddresses}
                  setDeliveryAddress={setDeliveryAddress}
                  currentStep={currentStep}
                  onEditAddress={onEditAddress}
                />
              </AddressList>
            )}
            {!canSaveDeliveryAddress && !enableSavedAddressesImprovements && (
              <WarningContainer>
                <InfoBox
                  info={formatMessage({ id: 'removeOne' })}
                  warning={formatMessage({ id: 'saveOrderMaximumNumberWarning' })}
                />
              </WarningContainer>
            )}
            {!showDeliveryAddresses &&
              !enableSavedAddressesImprovements &&
              addresses?.map((address: IDeliveryAddress, index: number) => (
                <DeliveryAddressItem
                  key={index}
                  addressLocation={{
                    address: { ...address, location: address.alias } as IPlaceAddress,
                    coordinates: {
                      lat: address.latitude as number,
                      lng: address.longitude as number,
                    },
                  }}
                  selected={index === selected}
                  onSelect={() => setSelected(index)}
                />
              ))}
          </AddressList>
          {!showDeliveryAddresses && !enableSavedAddressesImprovements && (
            <>
              <DeliveryTerms />
              <Box flexDirection="column" center padding="0 1rem">
                <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
                  <ActionButton
                    data-testid="select-delivery-address"
                    disabled={isLoading || selected < 0}
                    isLoading={isLoading}
                    onClick={handleSavedAddressLegacy}
                    fullWidth
                    variant={ActionButtonVariants.PRIMARY}
                  >
                    {formatMessage({ id: 'deliverHere' })}
                  </ActionButton>
                </Box>
              </Box>
            </>
          )}
          {isLoading && (
            <Box center>
              <LoadingText>{formatMessage({ id: 'deliveryCheckingMessage' })}</LoadingText>
            </Box>
          )}
        </>
      )}
      {isSavedAddressesStep && !showDeliveryAddresses && enableSavedAddressesImprovements && (
        <AddressList>
          <SavedAddresses
            key={`${currentStep}#${isMobile()}`}
            addresses={addresses}
            setAddress={handleSavedAddressSelect}
            setCurrentStep={setCurrentStep}
            initialCollapsed={false}
            setDeliveryAddress={setDeliveryAddress}
            currentStep={currentStep}
            onEditAddress={onEditAddress}
          />
        </AddressList>
      )}
      {addresses[selected] && (
        <ConfirmLocationModal
          streetName={addresses[selected].addressLine1}
          city={`${addresses[selected].city}, ${convertCountryStringToIsoCountryCode2(
            addresses[selected].country || ''
          )}`}
          isDelivery
          isGeolocationEnabled={isGeolocationEnabled}
          onConfirm={onConfirmDistanceWarning}
          onDismiss={onCancelDistanceWarning}
        />
      )}
    </>
  );
};
